/**
 * The shape of the sale JSON — `/promos/<slug>.json`, rendered by
 * public/templates/newdesign/blackfriday.json.
 *
 * These types describe a payload this code does not own: the endpoint is unchanged and
 * still serves the legacy page, so every field here is written to match what that
 * template emits rather than what a fresh design would ask for. Where a name reads
 * oddly — `hz`, `rp`, `msrp` meaning three different things by product type — the
 * comment says what the server means by it.
 */

/** A section of the sale. `icon` is a FontAwesome name without the `fa-` prefix. */
export type SaleCategory = {
  id: number;
  name: string;
  icon?: string | null;
};

/** One option of a RADIOBUTTONS variant — a term, a type, a pack size. */
export type VariantOption = {
  id: string | number;
  option: string;
  /** Percentage adjustment applied to the base price. Sent as a string. */
  percent?: string | number;
  /** Extra discount to advertise beside the term, as a whole percentage. */
  extraSavings?: number;
  /** 'Y' on the term the server would pick by default. */
  default?: string;
};

export type Variant = {
  name: string;
  title: string;
  options: VariantOption[];
};

export type SaleProductAttributes = {
  new_only: boolean;
  /**
   * The product is flagged `hide_from_hotzones`, on itself or on its section. Whether it
   * is actually hidden depends on the *visitor* and so is decided in the browser — see
   * shouldHideProduct.
   */
  hz: boolean;
  issues: number | null;
  language: string | null;
  country: string | null;
  is_subscription: boolean;
};

export type SaleProduct = {
  id: number;
  slug: string;
  category: { id: number; name: string };
  name: string;
  /** Cover price for a magazine, list price for a book, "purchased separately" for a bundle. */
  msrp: number;
  price: number;
  original_price: number;
  images: { normal: string; retina: string };
  product_type: number;
  attributes: SaleProductAttributes;
  is_digital: boolean;
  is_zinio: boolean;
  is_magzter: boolean;
  is_emags: boolean;
  is_bundle: boolean;
  merge_type_selectors: boolean;
  has_digital_instructions: boolean;
  digital_instructions?: string | null;
  benefits_instructions?: string | null;
  description: string;
  /** The offer code the sale applies to this product. Posted with the add-to-cart. */
  coupon_code?: string | null;
  restricted_terms: VariantOption[];
  /** Absolute price per term id, when the sale prices terms individually. */
  term_prices?: Record<string, number> | null;
  grouped_product_ids?: number[];
  variants: Variant[];
  /**
   * The product's active sku codes, keyed `"<termVariantId>_<typeVariantId>"`.
   *
   * The storefront cart api adds by sku code, and a tile knows only which Term and Type
   * options the customer picked — so this is what turns a selection into something that
   * can be bought without a round trip per product. Emitted by
   * `SkuManagerInterface::findVariantSkuCodesByProductIds` in one query for the sale.
   *
   * A combination with no entry is one the catalogue does not sell.
   */
  skus?: Record<string, string>;

  /**
   * The base sku's code — the id this product is reported to GA4 under.
   *
   * The code, not a sku: the sale is several hundred products and only the code is wanted.
   * A cart line carries the whole object instead, as `product.base_sku`, which is why this
   * one says `_code` — the two are the same value and must not read as the same shape.
   *
   * `null` on a product with no zero-percent active sku. The analytics module falls back
   * to the numeric id there, exactly as `tSys::trackingProductId()` does.
   */
  base_sku_code?: string | null;

  /**
   * Filled in by groupProducts(): the products merged into this tile, this one first.
   * Absent on a product that stands alone.
   */
  grouped_products?: SaleProduct[];
};

export type SalePayload = {
  categories: SaleCategory[];
  products: SaleProduct[];
};

/** Product type ids, as `DMagz\Model\ProductInterface` numbers them. */
export const PRODUCT_TYPE_MAGAZINE = 1;
export const PRODUCT_TYPE_POSTCARD = 3;
export const PRODUCT_TYPE_BOOK = 5;

/**
 * The slug of the Magzter Gold product, which the sale carries as an ordinary product
 * but which is never drawn as one — it gets its own full-width card above the grid.
 */
export const MAGZTER_GOLD_SLUG = 'magzter-gold';
